import axios from 'axios';
import { message as Message } from 'antd';
import { environment, token, cookie, gdssid, qs } from '@gaodun.com/gtools';
import { appId } from './config';
import i18n from '@/i18n';
import { ICustomAxiosConfig } from '@/types';

interface ICustomAxiosOptions extends ICustomAxiosConfig {
    isVisit?: boolean;
    withoutToken?: boolean;
} 

const http = axios.create();

http.interceptors.request.use(
    config => Promise.resolve(config),
    error => Promise.reject(error)
);

http.interceptors.response.use(
    response => {
        const { status, message, info } = response.data;
        // 登录失效处理
        if ((status > 553649000 && status < 563649999) || status === 10160001) {
            Message.error(message || info);
            setTimeout(() => {
                if (i18n.language === 'en') {
                    location.href = `https://${environment.getApiEnv()}www.areteup.com/login?redirect=${location.href}`;
                } else {
                    location.href = `https://${environment.getApiEnv()}v.gaodun.com/space/#/`;
                }
            }, i18n.language === 'en' ? 3000 : 5000);
            return Promise.resolve(response.data);
        }
        if (status !== 0) {
            Message.error(message || info || (i18n.language === 'en' ? 'Server Error' : '服务器错误'));
        }
        return Promise.resolve(response.data);
    },
    error => {
        if (error && error.response && error.response.status === 401) {
            Message.error(i18n.language === 'en' ? 'Login expired' : '登录失效');
        }
        return Promise.reject(error);
    }
);

const getDefaultOptions = (lang: string) => {
    if (lang === 'en') {
        return {
            baseURL: `https://${environment.getApiEnv()}apigateway.areteup.com`,
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                Authentication: '',
                'Accept-Language': 'en',
            },
            timeout: 20000,
            widthCredentials: true,
            transformRequest: (data: any) => data,
            isVisit: false,
            withoutToken: false,
        };
    }
    // zh
    return {
        baseURL: environment.getApigateWay(),
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
            Authentication: '',
            'Accept-Language': 'zh',
        },
        timeout: 20000,
        widthCredentials: true,
        transformRequest: (data: any) => data,
        isVisit: false,
        withoutToken: false,
    };
};

class Http {
    async request(options: any = {}): Promise<any> {
        const lang = i18n.language;
        const defaultOptions = getDefaultOptions(lang);

        let headers = {
            ...defaultOptions.headers,
            ...options.headers,
        };

        const g = await gdssid.get();

        if (!options.withoutToken) {
            if (lang === 'en') {
                // 英文版直接从cookie获取token
                const accessToken = cookie.getCookie('Accesstoken') || '';
                headers.Authentication = `Basic ${accessToken}`;
            } else {
                // 中文版用token.getFronttoken
                const accessToken = options.isVisit
                ? await token.getVisitToken({
                      GDSSID: g,
                      AppID: appId,
                  })
                : await token.getFronttoken({
                      AppID: appId,
                  });

                // const accessToken = await token.getFronttoken({ AppID: appId });
                headers.Authentication = `Basic ${accessToken}`;
            }
        }

        options = {
            ...defaultOptions,
            ...options,
            headers,
        };

        return http.request(options);
    }

    get<T>(url: string, params: T | {} = {}, customOptions: ICustomAxiosOptions = {}) {
        if (Array.isArray(params)) {
            for (const val of params) {
                url += `/${val}`;
            }
            params = {};
        }
        return this.request({
            method: 'get',
            url,
            params,
            ...customOptions,
        });
    }

    delete<T>(url: string, params: T | {} = {}, customOptions: ICustomAxiosOptions = {}) {
        if (Array.isArray(params)) {
            for (const val of params) {
                url += `/${val}`;
            }
            params = {};
        }
        return this.request({
            method: 'delete',
            url,
            params,
            ...customOptions,
        });
    }

    post<T>(url: string, data: T | string = '', customOptions: ICustomAxiosOptions = {}) {
        if (customOptions.headers && customOptions.headers['Content-Type'] === 'application/x-www-form-urlencoded') {
            data = qs.stringify(data as any);
        } else {
            if (data instanceof Object) {
                data = JSON.stringify(data);
            }
        }
        return this.request({
            method: 'post',
            url,
            data,
            ...customOptions,
        });
    }

    postQuery<T>(url: string, data: T, customOptions: ICustomAxiosOptions = {}) {
        return this.request({
            method: 'post',
            url,
            params: data,
            ...customOptions,
        });
    }

    put<T>(url: string, data: T | string = '', customOptions: ICustomAxiosOptions = {}) {
        if (data instanceof Object) {
            data = JSON.stringify(data);
        }
        return this.request({
            method: 'put',
            url,
            data,
            ...customOptions,
        });
    }
}

export default new Http();